import React, { useCallback, useEffect, useState } from "react";
import { API_URL } from "../../utils/config";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatNumero } from "../../utils/Utilitats";
import { mapejos, mapToPresentable } from "../../utils/Mapejos";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";

function esPackActiu(p) {
  return Number(p?.EsPack) === 1 && Number(p?.PackComposicioActiva) === 1;
}

function producteDeComponent(component) {
  return {
    idProductes: component.idProducte,
    Marca: component.marca,
    Model: component.model,
    Descripcio: component.descripcio,
    proveidors: component.proveidors || [],
  };
}

function nomPack(pack) {
  return nomProducte({ Marca: pack?.marca, Model: pack?.model, Descripcio: pack?.descripcio });
}

function nomProducte(p) {
  if (!p) return "—";
  const marca = [p.Marca, p.Model].filter((x) => x && String(x).trim()).join(" ").trim();
  return marca || p.Descripcio || "Producte";
}

function textReserva(reserva) {
  const quantitat = reserva.incoherent ? "composició no quadra" : formatNumero(reserva.quantitat);
  const unitat = reserva.unitat === "pack" && !reserva.incoherent ? " pack" : "";
  return `Reserva ${reserva.idReservesEstoc} · ${quantitat}${unitat} · ${reserva.estat}`;
}

const GRUPS_UBICACIO = [
  ["magatzems", "Magatzems"],
  ["vehicles", "Vehicles"],
  ["obres", "Obres"],
  ["altres", "Altres"],
];

function LogisticaTasca({ tasca }) {
  const { rolUsuari, empresaSeleccionada } = useUserContext();
  const potCrear = tincPermis(rolUsuari, "CREAR");
  const potModificar = tincPermis(rolUsuari, "MODIFICAR");
  const potEsborrar = tincPermis(rolUsuari, "ESBORRAR");
  const idEmpresa = empresaSeleccionada?.IdEmpresa || null;

  const [carregant, setCarregant] = useState(true);
  const [ocupat, setOcupat] = useState("");
  const [error, setError] = useState("");
  const [resum, setResum] = useState(null);
  const [linies, setLinies] = useState([]);
  const [assignacions, setAssignacions] = useState([]);
  const [packTasca, setPackTasca] = useState(null);
  const [productes, setProductes] = useState([]);
  const [formulari, setFormulari] = useState(null);
  const [reservaOberta, setReservaOberta] = useState(null);
  const [recepcioOberta, setRecepcioOberta] = useState(null);

  const cosBase = useCallback(
    () => ({ idTasques: tasca?.idTasques, idEmpresa }),
    [tasca?.idTasques, idEmpresa]
  );

  const carregar = useCallback(async () => {
    if (!tasca?.idTasques) return;
    setCarregant(true);
    setError("");
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Aprovisionamenttasca.php`, {
        method: "POST",
        body: JSON.stringify({ idTasques: tasca.idTasques, idEmpresa }),
      });
      const dades = await resposta.json();
      if (!dades.success) {
        setError(dades.message || "No s'ha pogut carregar la logística.");
        setResum(null);
        setLinies([]);
        setAssignacions([]);
        setPackTasca(null);
        return;
      }
      setResum(dades.resum || null);
      setLinies(dades.linies || []);
      setAssignacions(dades.assignacions || []);
      setPackTasca(dades.packTasca || null);

      const respostaProductes = await fetchAutenticat(`${API_URL}/Productescompatiblesaprovisionament.php`, {
        method: "POST",
        body: JSON.stringify({ idTasques: tasca.idTasques, idEmpresa }),
      });
      const dadesProductes = await respostaProductes.json();
      if (dadesProductes.success) {
        setProductes(dadesProductes.productes || []);
      } else if (dades.aprovisionable !== false) {
        setError(dadesProductes.message || "No s'han pogut carregar els productes.");
      }
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      setError("No s'ha pogut carregar la logística.");
      setAssignacions([]);
      setPackTasca(null);
    } finally {
      setCarregant(false);
    }
  }, [tasca?.idTasques, idEmpresa]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const refrescar = async () => {
    setFormulari(null);
    setReservaOberta(null);
    setRecepcioOberta(null);
    await carregar();
  };

  const actuarReserva = async (url, cos, textOcupat) => {
    if (ocupat) return;
    setOcupat(textOcupat);
    try {
      const resposta = await fetchAutenticat(`${API_URL}/${url}`, {
        method: "POST",
        body: JSON.stringify({ ...cosBase(), ...cos }),
      });
      const resultat = await resposta.json();
      if (!resultat.success) {
        alert(resultat.message || "No s'ha pogut actualitzar la reserva.");
        return;
      }
      await refrescar();
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      alert("No s'ha pogut actualitzar la reserva.");
    } finally {
      setOcupat("");
    }
  };

  const desar = async (dades) => {
    if (ocupat) return;
    const esNou = !dades.idAprovisionamentTasques;
    setOcupat(esNou ? "Desant…" : "Desant…");
    try {
      const resposta = await fetchAutenticat(
        `${API_URL}/${esNou ? "Nouaprovisionamenttasca.php" : "Modificaraprovisionamenttasca.php"}`,
        {
          method: "POST",
          body: JSON.stringify({
            ...cosBase(),
            ...dades,
            idAprovisionamentPacks: dades.idAprovisionamentPacks || formulari?.idAprovisionamentPacks || null,
          }),
        }
      );
      const resultat = await resposta.json();
      if (!resultat.success) {
        alert(resultat.message || "No s'ha pogut desar la línia.");
        return;
      }
      await refrescar();
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      alert("No s'ha pogut desar la línia.");
    } finally {
      setOcupat("");
    }
  };

  const eliminar = async (linia) => {
    if (ocupat) return;
    const producte = nomProducte(linia);
    if (!window.confirm(`Vols eliminar aquesta línia d'aprovisionament?\n${producte} · ${linia.Origen} · ${formatNumero(linia.Quantitat)}`)) {
      return;
    }
    setOcupat("Eliminant…");
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Esborraraprovisionamenttasca.php`, {
        method: "POST",
        body: JSON.stringify({
          ...cosBase(),
          idAprovisionamentTasques: linia.idAprovisionamentTasques,
        }),
      });
      const resultat = await resposta.json();
      if (!resultat.success) {
        alert(resultat.message || "No s'ha pogut eliminar la línia.");
        return;
      }
      await refrescar();
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      alert("No s'ha pogut eliminar la línia.");
    } finally {
      setOcupat("");
    }
  };

  const desarPack = async (dades) => {
    if (ocupat) return;
    setOcupat("Desant…");
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Guardaraprovisionamentpack.php`, {
        method: "POST",
        body: JSON.stringify({ ...cosBase(), ...dades }),
      });
      const resultat = await resposta.json();
      if (!resultat.success) {
        alert(resultat.message || "No s'ha pogut assignar el pack.");
        return;
      }
      await refrescar();
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      alert("No s'ha pogut assignar el pack.");
    } finally {
      setOcupat("");
    }
  };

  const esborrarPack = async (assignacio) => {
    if (ocupat) return;
    if (!window.confirm(`Vols treure l'assignació de ${nomProducte(assignacio)}?\n${formatNumero(assignacio.quantitat)} packs`)) {
      return;
    }
    setOcupat("Eliminant…");
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Esborraraprovisionamentpack.php`, {
        method: "POST",
        body: JSON.stringify({
          ...cosBase(),
          idAprovisionamentPacks: assignacio.idAprovisionamentPacks,
        }),
      });
      const resultat = await resposta.json();
      if (!resultat.success) {
        alert(resultat.message || "No s'ha pogut treure el pack.");
        return;
      }
      await refrescar();
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      alert("No s'ha pogut treure el pack.");
    } finally {
      setOcupat("");
    }
  };

  const necessitat = Number(resum?.necessitat) || 0;
  const pendent = Number(resum?.pendent) || 0;
  const productesNormals = productes.filter((p) => !esPackActiu(p));
  const packsLliures = productes.filter(
    (p) => esPackActiu(p) && !assignacions.some((a) => a.idProducte === p.idProductes)
  );
  const liniesNormals = linies.filter((l) => !l.idAprovisionamentPacks);
  const assignacionsMaterial = packTasca
    ? assignacions.filter((a) => a.idProducte !== packTasca.idProducte)
    : assignacions;
  const potAfegirLinia = potCrear && !packTasca && productesNormals.length > 0 && pendent > 0 && !formulari && !ocupat;
  const potAssignar = potCrear && !packTasca && packsLliures.length > 0 && pendent > 0 && !formulari && !ocupat;
  const producteFix = !packTasca && productesNormals.length === 1 ? productesNormals[0] : null;
  const mostrarPackTasca = !!(packTasca && (packTasca.idAprovisionamentPacks || Number(packTasca.quantitat) > 0));
  const hiHaContingut = liniesNormals.length > 0 || assignacionsMaterial.length > 0 || mostrarPackTasca;

  const obrirEdicioLinia = (linia) => {
    setReservaOberta(null);
    setRecepcioOberta(null);
    if (!linia.idAprovisionamentPacks) {
      setFormulari({ linia });
      return;
    }
    const assignacio =
      assignacions.find((a) => a.idAprovisionamentPacks === linia.idAprovisionamentPacks) ||
      (packTasca?.idAprovisionamentPacks === linia.idAprovisionamentPacks ? packTasca : null);
    const component = (assignacio?.components || []).find((c) => c.idProducte === linia.Productes_idProductes);
    setFormulari({
      component: true,
      linia,
      idAprovisionamentPacks: linia.idAprovisionamentPacks,
      productesComponent: component ? [producteDeComponent(component)] : [],
      pendentComponent: Number(component?.pendent) || 0,
    });
  };

  const propsLinia = {
    potModificar,
    potEsborrar,
    ocupat: !!ocupat,
    reservaOberta,
    recepcioOberta,
    idEmpresa,
    onEditar: obrirEdicioLinia,
    onEliminar: eliminar,
    onObrirReserva: (linia) => {
      setFormulari(null);
      setRecepcioOberta(null);
      setReservaOberta(linia.idAprovisionamentTasques);
    },
    onTancarReserva: () => setReservaOberta(null),
    onReservar: (linia, quantitat) =>
      actuarReserva(
        "Novareservaestoc.php",
        { idAprovisionamentTasques: linia.idAprovisionamentTasques, quantitat },
        "Reservant…"
      ),
    onObrirRecepcio: (linia) => {
      setFormulari(null);
      setReservaOberta(null);
      setRecepcioOberta(linia.idAprovisionamentTasques);
    },
    onTancarRecepcio: () => setRecepcioOberta(null),
    onRebre: (linia, dades) =>
      actuarReserva(
        "Rebreproducteaprovisionament.php",
        { idAprovisionamentTasques: linia.idAprovisionamentTasques, ...dades },
        "Rebent…"
      ),
  };

  const blocPack = (pack, editable) => (
    <BlocPack
      key={pack.idAprovisionamentPacks || `pack-${pack.idProducte}`}
      pack={pack}
      editable={editable}
      linies={linies}
      potCrear={potCrear && !formulari}
      potModificar={potModificar}
      potEsborrar={potEsborrar}
      ocupat={!!ocupat}
      reservaOberta={reservaOberta}
      recepcioOberta={recepcioOberta}
      idEmpresa={idEmpresa}
      onEditarQuantitat={() => setFormulari({ pack: true, assignacio: pack })}
      onEsborrar={() => esborrarPack(pack)}
      onAfegirComponent={(component) =>
        setFormulari({
          component: true,
          nou: true,
          idAprovisionamentPacks: pack.idAprovisionamentPacks || null,
          productesComponent: [producteDeComponent(component)],
          pendentComponent: Number(component.pendent) || 0,
        })
      }
      onEditarLinia={obrirEdicioLinia}
      onEliminarLinia={eliminar}
      onObrirReserva={propsLinia.onObrirReserva}
      onTancarReserva={propsLinia.onTancarReserva}
      onReservar={propsLinia.onReservar}
      onObrirRecepcio={propsLinia.onObrirRecepcio}
      onTancarRecepcio={propsLinia.onTancarRecepcio}
      onRebre={propsLinia.onRebre}
    />
  );
  const materialText = tasca?.NomMaterial || tasca?.DescripcioMaterial || "";
  const producteTasca = tasca?.Productes_idProductes
    ? [tasca.MarcaProducte, tasca.ModelProducte].filter((x) => x && String(x).trim()).join(" ").trim() ||
      tasca.DescripcioProducte ||
      ""
    : "";

  return (
    <div className="dv2-entity-body">
      <p className="dv2-hint">Tasca &gt; Logística</p>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <h2 className="dv2-card-title">Situació</h2>
        </div>
        {carregant && !resum ? (
          <p className="dv2-hint">Carregant…</p>
        ) : (
          <div className="dv2-fields dv2-fields--sizes">
            <div className="dv2-field dv2-field--curt">
              <span className="dv2-label">Necessitat</span>
              <span className="dv2-value">{formatNumero(resum?.necessitat)}</span>
            </div>
            <div className="dv2-field dv2-field--curt">
              <span className="dv2-label">Estoc</span>
              <span className="dv2-value">{formatNumero(resum?.estoc)}</span>
            </div>
            <div className="dv2-field dv2-field--curt">
              <span className="dv2-label">Proveïdor</span>
              <span className="dv2-value">{formatNumero(resum?.proveidor)}</span>
            </div>
            <div className="dv2-field dv2-field--curt">
              <span className="dv2-label">Packs</span>
              <span className="dv2-value">{formatNumero(resum?.packs)}</span>
            </div>
            <div className="dv2-field dv2-field--curt">
              <span className="dv2-label">Pendent</span>
              <span className="dv2-value">{formatNumero(resum?.pendent)}</span>
            </div>
          </div>
        )}
        {(materialText || producteTasca || !tasca?.Productes_idProductes) && (
          <p className="dv2-hint">
            {materialText ? `Material: ${materialText}. ` : ""}
            {tasca?.Productes_idProductes
              ? `Producte: ${producteTasca || "el de la tasca"}.`
              : "Producte: a definir per línia d'aprovisionament."}
          </p>
        )}
        {error ? <p className="dv2-banner dv2-banner--danger">{error}</p> : null}
        {necessitat <= 0 && resum ? (
          <p className="dv2-hint">La necessitat és 0. Es poden consultar les línies, però no afegir-ne.</p>
        ) : null}
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <h2 className="dv2-card-title">Línies</h2>
          {potAssignar && (
            <button
              type="button"
              className="dv2-btn"
              style={{ marginLeft: "auto" }}
              onClick={() => setFormulari({ pack: true })}
            >
              + Assignar pack
            </button>
          )}
          {potAfegirLinia && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              style={{ marginLeft: potAssignar ? 8 : "auto" }}
              onClick={() => setFormulari({ nou: true })}
            >
              + Afegir línia
            </button>
          )}
        </div>

        {ocupat ? <p className="dv2-hint">{ocupat}</p> : null}

        {formulari?.pack ? (
          <FormulariPack
            packs={
              formulari.assignacio
                ? [{ idProductes: formulari.assignacio.idProducte, Marca: formulari.assignacio.marca, Model: formulari.assignacio.model, Descripcio: formulari.assignacio.descripcio }]
                : packsLliures
            }
            assignacio={formulari.assignacio || null}
            maxim={formulari.assignacio ? formulari.assignacio.quantitatMaxima : pendent}
            ocupat={!!ocupat}
            onCancel={() => setFormulari(null)}
            onDesar={desarPack}
          />
        ) : null}

        {formulari && !formulari.pack ? (
          <FormulariLinia
            nou={!!formulari.nou}
            linia={formulari.linia || null}
            productes={formulari.component ? formulari.productesComponent || [] : productesNormals}
            producteFix={formulari.component ? formulari.productesComponent?.[0] || null : producteFix}
            pendent={formulari.component ? formulari.pendentComponent : pendent}
            ocupat={!!ocupat}
            onCancel={() => setFormulari(null)}
            onDesar={desar}
            onAlliberar={(idReserva) => {
              if (!window.confirm("Vols alliberar aquesta reserva? Deixarà de comprometre estoc i la fila es conservarà. Si ve d'una recepció, el material rebut es queda a l'estoc.")) return;
              actuarReserva("Alliberarreservaestoc.php", { idReservesEstoc: idReserva }, "Alliberant…");
            }}
          />
        ) : null}

        {packTasca && !packTasca.idAprovisionamentPacks && Number(packTasca.quantitat) <= 0 && Number(packTasca.quantitatLiniesAntigues) > 0 ? (
          <p className="dv2-hint">Les línies anteriors del pack ja cobreixen la necessitat.</p>
        ) : null}

        {mostrarPackTasca ? blocPack(packTasca, false) : null}
        {assignacionsMaterial.map((assignacio) => blocPack(assignacio, true))}

        {!hiHaContingut && !carregant ? (
          <p className="dv2-empty">
            {necessitat > 0
              ? "Encara no hi ha cap línia. El pendent és tota la necessitat."
              : "No hi ha línies d'aprovisionament."}
          </p>
        ) : liniesNormals.length > 0 ? (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  <th>Producte</th>
                  <th>Origen</th>
                  <th className="dv2-num">Quantitat</th>
                  <th>Proveïdor</th>
                  <th>Observacions</th>
                  <th className="dv2-table-actions">Accions</th>
                </tr>
              </thead>
              <tbody>
                {liniesNormals.map((linia) => (
                  <FilesLinia key={linia.idAprovisionamentTasques} linia={linia} {...propsLinia} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function DetallReserva({ linia, oberta, potModificar, ocupat, onObrir, onTancar, onReservar }) {
  const maxim = Math.min(Number(linia.pendentReserva) || 0, Number(linia.estocDisponible) || 0);
  const potReservar = potModificar && maxim > 0;
  const [quantitat, setQuantitat] = useState(maxim > 0 ? String(maxim) : "");

  return (
    <div>
      <p className="dv2-hint">
        Previst estoc: {formatNumero(linia.Quantitat)} · Reservat: {formatNumero(linia.reservatActiu)} · Pendent reserva:{" "}
        {formatNumero(linia.pendentReserva)}
      </p>
      <p className="dv2-hint">
        Estoc físic: {formatNumero(linia.estocFisic)} · Reservat total: {formatNumero(linia.estocReservat)} · Disponible:{" "}
        {formatNumero(linia.estocDisponible)}
      </p>
      {potReservar && !oberta && (
        <button type="button" className="dv2-btn" disabled={ocupat} onClick={onObrir}>
          Reservar
        </button>
      )}
      {oberta && (
        <form
          className="dv2-fields dv2-fields--sizes"
          onSubmit={(e) => {
            e.preventDefault();
            const q = Number(String(quantitat).replace(",", "."));
            if (!Number.isFinite(q) || q <= 0) {
              alert("La quantitat ha de ser superior a zero.");
              return;
            }
            if (q > maxim + 0.001) {
              alert(`El màxim que es pot reservar ara és ${formatNumero(maxim)}.`);
              return;
            }
            onReservar(q);
          }}
        >
          <label className="dv2-field dv2-field--curt">
            <span className="dv2-label">Quantitat a reservar</span>
            <input
              className="dv2-input"
              type="number"
              min="0.01"
              step="0.01"
              max={maxim}
              value={quantitat}
              onChange={(e) => setQuantitat(e.target.value)}
            />
            <span className="dv2-hint">Màxim ara: {formatNumero(maxim)}</span>
          </label>
          <div className="dv2-field dv2-field--mig">
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onTancar} disabled={ocupat}>
                Tancar
              </button>
              <button type="submit" className="dv2-btn dv2-btn--primary" disabled={ocupat}>
                Reservar
              </button>
            </div>
          </div>
        </form>
      )}
      {(linia.reserves || []).length > 0 && (
        <ul>
          {linia.reserves.map((reserva) => (
            <li key={reserva.idReservesEstoc}>{textReserva(reserva)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FormulariLinia({ nou, linia, productes, producteFix, pendent, ocupat, onCancel, onDesar, onAlliberar }) {
  const producteInicial = linia?.Productes_idProductes || producteFix?.idProductes || productes[0]?.idProductes || "";
  const [idProducte, setIdProducte] = useState(producteInicial ? String(producteInicial) : "");
  const [origen, setOrigen] = useState(linia?.Origen || "ESTOC");
  const quantitatInicial = linia ? linia.Quantitat : pendent > 0 ? pendent : 1;
  const [quantitat, setQuantitat] = useState(String(Math.round(Number(quantitatInicial) * 100) / 100));
  const [idProveidor, setIdProveidor] = useState(
    linia?.Productes_Proveidors_idProductesProveidors ? String(linia.Productes_Proveidors_idProductesProveidors) : ""
  );
  const [observacions, setObservacions] = useState(linia?.Observacions || "");

  const opcions =
    linia && !productes.some((p) => String(p.idProductes) === String(linia.Productes_idProductes))
      ? [
          {
            idProductes: linia.Productes_idProductes,
            Marca: linia.Marca,
            Model: linia.Model,
            Descripcio: linia.Descripcio,
            proveidors: [],
          },
          ...productes,
        ]
      : productes;
  const producte = opcions.find((p) => String(p.idProductes) === String(idProducte));
  const proveidorsBase = producte?.proveidors || [];
  const proveidors =
    linia?.Productes_Proveidors_idProductesProveidors &&
    !proveidorsBase.some(
      (p) => String(p.idProductesProveidors) === String(linia.Productes_Proveidors_idProductesProveidors)
    )
      ? [
          {
            idProductesProveidors: linia.Productes_Proveidors_idProductesProveidors,
            NomProveidor: linia.NomProveidor,
          },
          ...proveidorsBase,
        ]
      : proveidorsBase;
  const disponible = nou ? pendent : pendent + (Number(linia?.Quantitat) || 0);

  const canviarOrigen = (valor) => {
    setOrigen(valor);
    if (valor === "ESTOC") setIdProveidor("");
  };

  const enviar = (event) => {
    event.preventDefault();
    const q = Number(String(quantitat).replace(",", "."));
    if (!idProducte) {
      alert("Cal un producte.");
      return;
    }
    if (!Number.isFinite(q) || q <= 0) {
      alert("La quantitat ha de ser superior a zero.");
      return;
    }
    onDesar({
      idAprovisionamentTasques: linia?.idAprovisionamentTasques || null,
      Productes_idProductes: Number(idProducte),
      Quantitat: q,
      Origen: origen,
      Productes_Proveidors_idProductesProveidors: origen === "PROVEIDOR" && idProveidor ? Number(idProveidor) : null,
      Observacions: observacions.trim() || null,
    });
  };

  return (
    <form className="dv2-fields dv2-fields--sizes" onSubmit={enviar}>
      <label className="dv2-field dv2-field--llarg">
        <span className="dv2-label">Producte</span>
        {opcions.length <= 1 || linia?.teReserves || linia?.teRecepcions ? (
          <span className="dv2-value">{nomProducte(producte || producteFix || linia)}</span>
        ) : (
          <select className="dv2-select" value={idProducte} onChange={(e) => { setIdProducte(e.target.value); setIdProveidor(""); }}>
            {opcions.map((p) => (
              <option key={p.idProductes} value={p.idProductes}>
                {nomProducte(p)}
              </option>
            ))}
          </select>
        )}
      </label>
      <label className="dv2-field dv2-field--mig">
        <span className="dv2-label">Origen</span>
        <select
          className="dv2-select"
          value={origen}
          disabled={!!linia?.teRecepcions}
          onChange={(e) => canviarOrigen(e.target.value)}
        >
          <option value="ESTOC">Estoc</option>
          <option value="PROVEIDOR" disabled={linia?.Origen === "ESTOC" && (Number(linia?.reservatActiu) || 0) > 0}>
            Proveïdor
          </option>
        </select>
      </label>
      <label className="dv2-field dv2-field--curt">
        <span className="dv2-label">Quantitat</span>
        <input
          className="dv2-input"
          type="number"
          min="0.01"
          step="0.01"
          value={quantitat}
          onChange={(e) => setQuantitat(e.target.value)}
        />
        <span className="dv2-hint">
          {nou ? "Pendent actual" : "Disponible per a aquesta línia"}: {formatNumero(disponible)}
          {linia?.teRecepcions ? `. Ja rebut: ${formatNumero(linia.rebutAcumulat)}` : ""}
        </span>
      </label>
      {origen === "PROVEIDOR" && (
        <label className="dv2-field dv2-field--llarg">
          <span className="dv2-label">Proveïdor</span>
          <select className="dv2-select" value={idProveidor} onChange={(e) => setIdProveidor(e.target.value)}>
            <option value="">Sense proveïdor concret</option>
            {proveidors.map((p) => (
              <option key={p.idProductesProveidors} value={p.idProductesProveidors}>
                {p.NomProveidor || "Proveïdor"}
                {p.PreuNet != null ? ` · ${formatNumero(p.PreuNet)}` : ""}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="dv2-field dv2-field--ample">
        <span className="dv2-label">Observacions</span>
        <input
          className="dv2-input"
          type="text"
          maxLength={255}
          value={observacions}
          onChange={(e) => setObservacions(e.target.value)}
        />
      </label>
      {!nou && (linia?.reserves || []).some((reserva) => reserva.estat === "ACTIVA") && (
        <div className="dv2-field dv2-field--ample">
          <span className="dv2-label">Reserves actives</span>
          <ul>
            {linia.reserves
              .filter((reserva) => reserva.estat === "ACTIVA")
              .map((reserva) => (
                <li key={reserva.idReservesEstoc}>
                  {textReserva(reserva)}{" "}
                  <button
                    type="button"
                    className="dv2-btn"
                    disabled={ocupat}
                    onClick={() => onAlliberar(reserva.idReservesEstoc)}
                  >
                    Alliberar reserva
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
      <div className="dv2-field dv2-field--ample">
        <div className="dv2-footerActions">
          <button type="button" className="dv2-btn" onClick={onCancel} disabled={ocupat}>
            Tancar
          </button>
          <button type="submit" className="dv2-btn dv2-btn--primary" disabled={ocupat}>
            {ocupat ? "Desant…" : nou ? "Afegir" : "Desar"}
          </button>
        </div>
      </div>
    </form>
  );
}

function DetallRecepcio({ linia, oberta, potModificar, ocupat, idEmpresa, onObrir, onTancar, onRebre }) {
  const pendent = Number(linia.pendentRebre) || 0;
  const potRebre = potModificar && pendent > 0 && !linia.recepcioIncoherent;
  const unitat = linia.packCompost ? " pack" : "";
  const [quantitat, setQuantitat] = useState(pendent > 0 ? String(pendent) : "");
  const [idUbicacio, setIdUbicacio] = useState("");
  const [observacions, setObservacions] = useState("");
  const [ubicacions, setUbicacions] = useState(null);
  const [errorUbicacions, setErrorUbicacions] = useState("");
  const [altaAltre, setAltaAltre] = useState(false);
  const [nomAltre, setNomAltre] = useState("");
  const [adreca, setAdreca] = useState({
    TipusVia: "",
    NomVia: "",
    NumVia: "",
    Poblacio: "",
    CodiPostal: "",
    Provincia: "",
    Pais: "",
  });

  const carregarUbicacions = useCallback(async () => {
    setErrorUbicacions("");
    try {
      const resposta = await fetchAutenticat(`${API_URL}/Ubicacions.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa }),
      });
      const dades = await resposta.json();
      if (!dades.success) {
        setErrorUbicacions(dades.message || "No s'han pogut carregar les ubicacions.");
        setUbicacions(null);
        return null;
      }
      setUbicacions(dades);
      return dades;
    } catch (e) {
      if (e instanceof SessioCaducadaError) return null;
      setErrorUbicacions("No s'han pogut carregar les ubicacions.");
      return null;
    }
  }, [idEmpresa]);

  useEffect(() => {
    if (!oberta) return;
    setQuantitat(pendent > 0 ? String(pendent) : "");
    carregarUbicacions();
  }, [oberta, carregarUbicacions, pendent]);

  const crearAltre = async () => {
    if (ocupat) return;
    if (!nomAltre.trim()) {
      alert("Cal un nom per a la ubicació.");
      return;
    }
    try {
      const resposta = await fetchAutenticat(`${API_URL}/NouUbicacioAltre.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          Nom: nomAltre.trim(),
          ...adreca,
        }),
      });
      const dades = await resposta.json();
      if (!dades.success) {
        alert(dades.message || "No s'ha pogut crear la ubicació.");
        return;
      }
      await carregarUbicacions();
      setIdUbicacio(String(dades.idUbicacions));
      setAltaAltre(false);
      setNomAltre("");
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      alert("No s'ha pogut crear la ubicació.");
    }
  };

  const quantitatNumero = Number(String(quantitat).replace(",", "."));
  const previsualitzacio =
    linia.packCompost && Number.isFinite(quantitatNumero) && quantitatNumero > 0
      ? (linia.componentsPack || []).map((component) => ({
          nom: component.nom,
          quantitat: quantitatNumero * Number(component.quantitat),
        }))
      : [];

  return (
    <div>
      <p className="dv2-hint">
        Previst proveïdor: {formatNumero(linia.Quantitat)}
        {unitat} · Rebut: {linia.recepcioIncoherent ? "composició no quadra" : `${formatNumero(linia.rebutAcumulat)}${unitat}`} · Pendent:{" "}
        {formatNumero(linia.pendentRebre)}
        {unitat} · Reservat: {linia.reservaIncoherent ? "composició no quadra" : `${formatNumero(linia.reservatActiu)}${unitat}`}
      </p>
      {linia.recepcioIncoherent ? (
        <p className="dv2-hint">La recepció no quadra amb la composició del pack. No se'n pot rebre més fins a aclarir-ho.</p>
      ) : null}
      {potRebre && !oberta && (
        <button type="button" className="dv2-btn" disabled={ocupat} onClick={onObrir}>
          Rebre producte
        </button>
      )}
      {oberta && (
        <form
          className="dv2-fields dv2-fields--sizes"
          onSubmit={(e) => {
            e.preventDefault();
            if (!Number.isFinite(quantitatNumero) || quantitatNumero <= 0) {
              alert("La quantitat ha de ser superior a zero.");
              return;
            }
            if (quantitatNumero > pendent + 0.001) {
              alert(`El màxim que es pot rebre ara és ${formatNumero(pendent)}.`);
              return;
            }
            if (!idUbicacio) {
              alert("Cal una ubicació de destí.");
              return;
            }
            onRebre({
              quantitat: quantitatNumero,
              idUbicacions: Number(idUbicacio),
              observacions: observacions.trim() || null,
            });
          }}
        >
          <label className="dv2-field dv2-field--curt">
            <span className="dv2-label">Quantitat rebuda</span>
            <input
              className="dv2-input"
              type="number"
              min="0.01"
              step="0.01"
              max={pendent}
              value={quantitat}
              onChange={(e) => setQuantitat(e.target.value)}
            />
            <span className="dv2-hint">Pendent: {formatNumero(pendent)}{unitat}</span>
          </label>
          <label className="dv2-field dv2-field--llarg">
            <span className="dv2-label">Ubicació destí</span>
            <select className="dv2-select" value={idUbicacio} onChange={(e) => setIdUbicacio(e.target.value)}>
              <option value="">Tria una ubicació</option>
              {GRUPS_UBICACIO.map(([clau, etiqueta]) => {
                const items = ubicacions?.[clau] || [];
                if (!items.length) return null;
                return (
                  <optgroup key={clau} label={etiqueta}>
                    {items.map((item) => (
                      <option key={item.idUbicacions} value={item.idUbicacions}>
                        {item.nom}
                        {item.adreca ? ` · ${item.adreca}` : ""}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            {errorUbicacions ? <span className="dv2-hint">{errorUbicacions}</span> : null}
          </label>
          <label className="dv2-field dv2-field--ample">
            <span className="dv2-label">Observacions</span>
            <input
              className="dv2-input"
              type="text"
              maxLength={255}
              value={observacions}
              onChange={(e) => setObservacions(e.target.value)}
            />
          </label>
          {previsualitzacio.length > 0 && (
            <div className="dv2-field dv2-field--ample">
              <span className="dv2-hint">
                {formatNumero(quantitatNumero)} packs equivalen a:{" "}
                {previsualitzacio.map((component) => `${formatNumero(component.quantitat)} × ${component.nom}`).join(", ")}
              </span>
            </div>
          )}
          <div className="dv2-field dv2-field--ample">
            <button type="button" className="dv2-btn" disabled={ocupat} onClick={() => setAltaAltre((valor) => !valor)}>
              Nova ubicació
            </button>
          </div>
          {altaAltre && (
            <>
              <label className="dv2-field dv2-field--llarg">
                <span className="dv2-label">Nom</span>
                <input className="dv2-input" type="text" maxLength={100} value={nomAltre} onChange={(e) => setNomAltre(e.target.value)} />
              </label>
              <label className="dv2-field dv2-field--mig">
                <span className="dv2-label">Tipus de via</span>
                <select
                  className="dv2-select"
                  value={adreca.TipusVia}
                  onChange={(e) => setAdreca((prev) => ({ ...prev, TipusVia: e.target.value }))}
                >
                  <option value="">Selecciona</option>
                  {Object.keys(mapejos.tipusVia).map((option) => (
                    <option key={option} value={option}>
                      {mapToPresentable(option, mapejos.tipusVia)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="dv2-field dv2-field--llarg">
                <span className="dv2-label">Nom de la via</span>
                <input
                  className="dv2-input"
                  type="text"
                  maxLength={100}
                  value={adreca.NomVia}
                  onChange={(e) => setAdreca((prev) => ({ ...prev, NomVia: e.target.value }))}
                />
              </label>
              <label className="dv2-field dv2-field--curt">
                <span className="dv2-label">Número</span>
                <input
                  className="dv2-input"
                  type="text"
                  maxLength={15}
                  value={adreca.NumVia}
                  onChange={(e) => setAdreca((prev) => ({ ...prev, NumVia: e.target.value }))}
                />
              </label>
              <label className="dv2-field dv2-field--mig">
                <span className="dv2-label">Població</span>
                <input
                  className="dv2-input"
                  type="text"
                  maxLength={100}
                  value={adreca.Poblacio}
                  onChange={(e) => setAdreca((prev) => ({ ...prev, Poblacio: e.target.value }))}
                />
              </label>
              <label className="dv2-field dv2-field--curt">
                <span className="dv2-label">Codi postal</span>
                <input
                  className="dv2-input"
                  type="text"
                  maxLength={10}
                  value={adreca.CodiPostal}
                  onChange={(e) => setAdreca((prev) => ({ ...prev, CodiPostal: e.target.value }))}
                />
              </label>
              <label className="dv2-field dv2-field--mig">
                <span className="dv2-label">Província</span>
                <input
                  className="dv2-input"
                  type="text"
                  maxLength={50}
                  value={adreca.Provincia}
                  onChange={(e) => setAdreca((prev) => ({ ...prev, Provincia: e.target.value }))}
                />
              </label>
              <label className="dv2-field dv2-field--mig">
                <span className="dv2-label">País</span>
                <input
                  className="dv2-input"
                  type="text"
                  maxLength={100}
                  value={adreca.Pais}
                  onChange={(e) => setAdreca((prev) => ({ ...prev, Pais: e.target.value }))}
                />
              </label>
              <div className="dv2-field dv2-field--mig">
                <button type="button" className="dv2-btn" disabled={ocupat} onClick={crearAltre}>
                  Crear ubicació
                </button>
              </div>
            </>
          )}
          <div className="dv2-field dv2-field--mig">
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onTancar} disabled={ocupat}>
                Tancar
              </button>
              <button type="submit" className="dv2-btn dv2-btn--primary" disabled={ocupat}>
                Rebre
              </button>
            </div>
          </div>
        </form>
      )}
      {(linia.reserves || []).length > 0 && (
        <ul>
          {linia.reserves.map((reserva) => (
            <li key={reserva.idReservesEstoc}>{textReserva(reserva)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilesLinia({
  linia,
  potModificar,
  potEsborrar,
  ocupat,
  reservaOberta,
  recepcioOberta,
  idEmpresa,
  onEditar,
  onEliminar,
  onObrirReserva,
  onTancarReserva,
  onReservar,
  onObrirRecepcio,
  onTancarRecepcio,
  onRebre,
}) {
  return (
    <React.Fragment>
      <tr>
        <td>{nomProducte(linia)}</td>
        <td>{linia.Origen === "ESTOC" ? "Estoc" : "Proveïdor"}</td>
        <td className="dv2-num">{formatNumero(linia.Quantitat)}</td>
        <td>{linia.NomProveidor || "—"}</td>
        <td>{linia.Observacions || "—"}</td>
        <td className="dv2-table-actions">
          {potModificar && (
            <button type="button" className="dv2-btn" disabled={ocupat} onClick={() => onEditar(linia)}>
              Editar
            </button>
          )}
          {potEsborrar && !linia.teReserves && !linia.teRecepcions && (
            <button type="button" className="dv2-btn dv2-btn--danger" disabled={ocupat} onClick={() => onEliminar(linia)}>
              Eliminar
            </button>
          )}
        </td>
      </tr>
      {linia.Origen === "ESTOC" && (
        <tr>
          <td colSpan={6}>
            <DetallReserva
              linia={linia}
              oberta={reservaOberta === linia.idAprovisionamentTasques}
              potModificar={potModificar}
              ocupat={ocupat}
              onObrir={() => onObrirReserva(linia)}
              onTancar={onTancarReserva}
              onReservar={(quantitat) => onReservar(linia, quantitat)}
            />
          </td>
        </tr>
      )}
      {linia.Origen === "PROVEIDOR" && (
        <tr>
          <td colSpan={6}>
            <DetallRecepcio
              linia={linia}
              oberta={recepcioOberta === linia.idAprovisionamentTasques}
              potModificar={potModificar}
              ocupat={ocupat}
              idEmpresa={idEmpresa}
              onObrir={() => onObrirRecepcio(linia)}
              onTancar={onTancarRecepcio}
              onRebre={(dades) => onRebre(linia, dades)}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function FormulariPack({ packs, assignacio, maxim, ocupat, onCancel, onDesar }) {
  const [idProducte, setIdProducte] = useState(String(assignacio?.idProducte || packs[0]?.idProductes || ""));
  const inicial = assignacio ? assignacio.quantitat : maxim > 0 ? maxim : 1;
  const [quantitat, setQuantitat] = useState(String(Math.round(Number(inicial) * 100) / 100));
  const producte = packs.find((p) => String(p.idProductes) === String(idProducte));

  return (
    <form
      className="dv2-fields dv2-fields--sizes"
      onSubmit={(event) => {
        event.preventDefault();
        const q = Number(String(quantitat).replace(",", "."));
        if (!idProducte) {
          alert("Cal un pack.");
          return;
        }
        if (!Number.isFinite(q) || q <= 0) {
          alert("La quantitat ha de ser superior a zero.");
          return;
        }
        if (q > Number(maxim) + 0.001) {
          alert(`El màxim que es pot assignar és ${formatNumero(maxim)}.`);
          return;
        }
        onDesar({ idProductes: Number(idProducte), quantitat: q });
      }}
    >
      <label className="dv2-field dv2-field--llarg">
        <span className="dv2-label">Pack</span>
        {assignacio || packs.length <= 1 ? (
          <span className="dv2-value">{nomProducte(producte || { Marca: assignacio?.marca, Model: assignacio?.model, Descripcio: assignacio?.descripcio })}</span>
        ) : (
          <select className="dv2-select" value={idProducte} onChange={(e) => setIdProducte(e.target.value)}>
            {packs.map((p) => (
              <option key={p.idProductes} value={p.idProductes}>
                {nomProducte(p)}
              </option>
            ))}
          </select>
        )}
      </label>
      <label className="dv2-field dv2-field--curt">
        <span className="dv2-label">Quantitat de packs</span>
        <input
          className="dv2-input"
          type="number"
          min="0.01"
          step="0.01"
          max={maxim}
          value={quantitat}
          onChange={(e) => setQuantitat(e.target.value)}
        />
        <span className="dv2-hint">Màxim: {formatNumero(maxim)}</span>
      </label>
      <div className="dv2-field dv2-field--ample">
        <div className="dv2-footerActions">
          <button type="button" className="dv2-btn" onClick={onCancel} disabled={ocupat}>
            Tancar
          </button>
          <button type="submit" className="dv2-btn dv2-btn--primary" disabled={ocupat}>
            {ocupat ? "Desant…" : "Desar"}
          </button>
        </div>
      </div>
    </form>
  );
}

function BlocPack({
  pack,
  editable,
  linies,
  potCrear,
  potModificar,
  potEsborrar,
  ocupat,
  reservaOberta,
  recepcioOberta,
  idEmpresa,
  onEditarQuantitat,
  onEsborrar,
  onAfegirComponent,
  onEditarLinia,
  onEliminarLinia,
  onObrirReserva,
  onTancarReserva,
  onReservar,
  onObrirRecepcio,
  onTancarRecepcio,
  onRebre,
}) {
  const teLinies = linies.some((linia) => linia.idAprovisionamentPacks === pack.idAprovisionamentPacks);
  return (
    <div style={{ marginTop: 16 }}>
      <div className="dv2-card-head">
        <h3 className="dv2-card-title">
          Pack {nomPack(pack)} × {formatNumero(pack.quantitat)}
        </h3>
        <span className="dv2-hint" style={{ marginLeft: 12 }}>
          {pack.resolt ? "Resolt" : "Pendent"}
        </span>
        {editable && potModificar && (
          <button type="button" className="dv2-btn" style={{ marginLeft: "auto" }} disabled={ocupat} onClick={onEditarQuantitat}>
            Editar quantitat
          </button>
        )}
        {editable && potEsborrar && pack.idAprovisionamentPacks && !teLinies && (
          <button type="button" className="dv2-btn dv2-btn--danger" disabled={ocupat} onClick={onEsborrar}>
            Treure
          </button>
        )}
      </div>
      {Number(pack.quantitatLiniesAntigues) > 0 && (
        <p className="dv2-hint">
          Les línies anteriors del pare cobreixen {formatNumero(pack.quantitatLiniesAntigues)}. Aquesta assignació cobreix la resta.
        </p>
      )}
      {(pack.components || []).map((component) => {
        const seves = linies.filter(
          (linia) =>
            linia.idAprovisionamentPacks === pack.idAprovisionamentPacks &&
            linia.Productes_idProductes === component.idProducte
        );
        return (
          <div key={component.idProducte} style={{ marginTop: 8 }}>
            <p className="dv2-hint">
              {nomPack(component)} · Necessari {formatNumero(component.necessari)} · Estoc {formatNumero(component.estoc)} ·
              Proveïdor {formatNumero(component.proveidor)} · Pendent {formatNumero(component.pendent)}
            </p>
            <p className="dv2-hint">
              Estoc físic {formatNumero(component.estocFisic)} · Reservat {formatNumero(component.estocReservat)} · Disponible{" "}
              {formatNumero(component.estocDisponible)}
            </p>
            {potCrear && Number(component.pendent) > 0 && (
              <button type="button" className="dv2-btn" disabled={ocupat} onClick={() => onAfegirComponent(component)}>
                + Línia de component
              </button>
            )}
            {seves.length > 0 && (
              <div className="dv2-tableWrap">
                <table className="dv2-table">
                  <thead>
                    <tr>
                      <th>Producte</th>
                      <th>Origen</th>
                      <th className="dv2-num">Quantitat</th>
                      <th>Proveïdor</th>
                      <th>Observacions</th>
                      <th className="dv2-table-actions">Accions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seves.map((linia) => (
                      <FilesLinia
                        key={linia.idAprovisionamentTasques}
                        linia={linia}
                        potModificar={potModificar}
                        potEsborrar={potEsborrar}
                        ocupat={ocupat}
                        reservaOberta={reservaOberta}
                        recepcioOberta={recepcioOberta}
                        idEmpresa={idEmpresa}
                        onEditar={onEditarLinia}
                        onEliminar={onEliminarLinia}
                        onObrirReserva={onObrirReserva}
                        onTancarReserva={onTancarReserva}
                        onReservar={onReservar}
                        onObrirRecepcio={onObrirRecepcio}
                        onTancarRecepcio={onTancarRecepcio}
                        onRebre={onRebre}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default LogisticaTasca;
